// PROD-CUSTO-RLS (I.167, ato de tela 15a): ponto único de leitura de custo no front.
// No Ato 2 do banco o papel do app perde a leitura das colunas de custo de products, stock_movements, stock_entries e
// product_suppliers; toda leitura "todas as colunas" dessas tabelas passa a dar "permissão negada" para todos.
// Por isso: as listas pedem só as colunas abaixo (sem custo) e o custo é lido só aqui, pelas funções do banco com a
// chave (push colado ao APPLY).
import { supabase } from '@/integrations/supabase/client';
import { fetchAllRows, SERVER_PAGE_SIZE } from '@/lib/fetchAllRows';

// Listas em texto literal de uma linha só: o Supabase deduz o tipo da linha pelo texto (concatenado, perde o tipo).
// As 52 colunas de products que não são custo (fora: cost_price, average_cost, last_cost), na ordem do banco
// (LARI-GTIN-INTERNO, I.178: + internal_barcode, código de barras interno — leitura liberada na mesma migration)
export const PRODUCT_COLUMNS = 'id, company_id, name, sku, description, product_type, product_format, measurement_unit_id, category, brand, sale_price, stock_quantity, min_stock, max_stock, available_for_rental, available_for_sale, capacity_liters, active, created_at, updated_at, created_by, rental_stock_quantity, requires_serial_tracking, rental_items_count, ean, supplier_id, rental_price, min_sale_price, min_rental_price, ncm, cest, fiscal_product_type, fiscal_origin, price_table_id, cfop_padrao, icms_cst, pis_cst, cofins_cst, deleted_at, deleted_by, product_type_id, has_bom, is_manufactured, reserved_stock_quantity, category_id, model_id, variant_key, weight_kg, width_cm, height_cm, length_cm, internal_barcode';

// As 26 colunas de stock_movements que não são custo (fora: unit_cost, total_cost, average_cost_after)
export const STOCK_MOVEMENT_COLUMNS = 'id, company_id, movement_number, movement_type, movement_date, product_id, quantity, stock_after, reference_type, reference_id, deposit_id, from_deposit_id, to_deposit_id, supplier_id, customer_id, reason, notes, created_by, created_at, updated_at, expense_id, cost_center_id, expense_category_id, purchase_item_id, deposit_stock_after, sale_item_id';

// As 11 colunas de stock_entries e as 15 de product_suppliers que não são custo
export const STOCK_ENTRY_COLUMNS = 'id, entry_number, product_type, product_id, quantity, entry_date, notes, created_at, updated_at, created_by, company_id';
export const PRODUCT_SUPPLIER_COLUMNS = 'id, product_id, supplier_id, company_id, last_purchase_date, supplier_sku, supplier_description, lead_time_days, min_order_quantity, is_preferred, priority, active, notes, created_at, updated_at';

export interface ProductCosts {
  cost_price: number | null;
  average_cost: number | null;
  last_cost: number | null;
}

export interface StockMovementCosts {
  unit_cost: number | null;
  total_cost: number | null;
  average_cost_after: number | null;
}

// PROD-CUSTO-RLS (push colado ao APPLY do Ato 2, I.167): a fonte do custo são as funções do banco com a chave de
// preços — get_product_costs, get_stock_movement_costs e get_product_ids_by_cost (respostas 8a, 19a, 20a). A empresa é a
// do usuário, pela própria função. Chamada pelo corpo da requisição (sem limite de ids no endereço).
// O servidor devolve no máximo 1.000 linhas por chamada: o custo vai em lotes de até 1.000 ids, e a lista por custo é
// lida em páginas de 1.000 pela ordem da função. Chamadas tipadas pelos tipos regenerados depois do APPLY.

const uniqueIds = (ids: string[]) => Array.from(new Set(ids.filter(Boolean)));

const batches = (ids: string[]): string[][] => {
  const out: string[][] = [];
  for (let i = 0; i < ids.length; i += SERVER_PAGE_SIZE) out.push(ids.slice(i, i + SERVER_PAGE_SIZE));
  return out;
};

const toNumber = (v: unknown): number | null => (v === null || v === undefined ? null : Number(v));

// Custo dos produtos por id (sem a chave a função devolve os três custos vazios; quem chama ainda confere useCanSeeCost)
export const fetchProductCosts = async (companyId: string, ids: string[]): Promise<Record<string, ProductCosts>> => {
  const result: Record<string, ProductCosts> = {};
  const list = uniqueIds(ids);
  if (!companyId || list.length === 0) return result;
  const pages = await Promise.all(batches(list).map(part => supabase.rpc('get_product_costs', { p_product_ids: part })));
  for (const { data, error } of pages as { data: unknown; error: { message: string } | null }[]) {
    if (error) throw error;
    for (const row of (data as { product_id: string; cost_price: unknown; average_cost: unknown; last_cost: unknown }[]) || []) {
      result[row.product_id] = {
        cost_price: toNumber(row.cost_price),
        average_cost: toNumber(row.average_cost),
        last_cost: toNumber(row.last_cost),
      };
    }
  }
  return result;
};

// Custo dos movimentos de estoque por id (vazio sem a chave)
export const fetchStockMovementCosts = async (companyId: string, ids: string[]): Promise<Record<string, StockMovementCosts>> => {
  const result: Record<string, StockMovementCosts> = {};
  const list = uniqueIds(ids);
  if (!companyId || list.length === 0) return result;
  const pages = await Promise.all(batches(list).map(part => supabase.rpc('get_stock_movement_costs', { p_movement_ids: part })));
  for (const { data, error } of pages as { data: unknown; error: { message: string } | null }[]) {
    if (error) throw error;
    for (const row of (data as { movement_id: string; unit_cost: unknown; total_cost: unknown; average_cost_after: unknown }[]) || []) {
      result[row.movement_id] = {
        unit_cost: toNumber(row.unit_cost),
        total_cost: toNumber(row.total_cost),
        average_cost_after: toNumber(row.average_cost_after),
      };
    }
  }
  return result;
};

// Resposta 19a: filtro "Custo pendente" (na ordem recebida) e ordem por custo médio, pela chave; sem a chave o banco
// recusa ("Acesso negado"). Devolve os ids já na ordem, lidos em páginas de 1.000 pela posição que a função dá.
export const fetchProductIdsByCost = async (
  ids: string[],
  mode: 'cost_pending' | 'average_cost',
  sortDir: 'asc' | 'desc' = 'asc',
): Promise<string[]> => {
  if (ids.length === 0) return [];
  const rows = await fetchAllRows<{ product_id: string; position: number }>((from, to) =>
    supabase.rpc('get_product_ids_by_cost', { p_product_ids: ids, p_mode: mode, p_sort_dir: sortDir })
      .order('position', { ascending: true })
      .range(from, to));
  return rows.map(r => r.product_id);
};
